import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import {
  searchCameras,
  searchAiExposure,
  lookupAiExposureIp,
  searchBdSec,
  searchAgentExposure,
  checkBypass,
  startScan,
  subscribeScan,
  probeTarget,
  CameraResult,
  AiExposureResult,
  AgentExposureResult,
  BdSecResult,
  ProbeResult,
} from "../api";
import {
  API_CATALOG,
  ApiStatus,
  Scan,
  TIER_ORDER,
  TIER_LABEL,
} from "../types";

const COUNTRY_CENTROIDS: Record<string, [number, number]> = {
  US: [-98.5, 39.8], CN: [104.2, 35.8], RU: [105.3, 61.5],
  DE: [10.4, 51.2], FR: [2.2, 46.2], GB: [-3.4, 55.4],
  JP: [138.3, 36.2], BR: [-51.9, -14.2], IN: [78.9, 20.6],
  AU: [133.8, -25.3], CA: [-106.3, 56.1], KR: [127.8, 35.9],
  IT: [12.6, 41.9], ES: [-3.7, 40.5], NL: [5.3, 52.1],
  SE: [18.6, 60.1], NO: [8.5, 60.5], FI: [25.7, 61.9],
  PL: [19.1, 51.9], CH: [8.2, 46.8], AT: [14.6, 47.5],
  BE: [4.5, 50.5], PT: [-8.2, 39.4], CZ: [15.5, 49.8],
  DK: [9.5, 56.3], IE: [-8.2, 53.4], MX: [-102.6, 23.6],
  AR: [-63.6, -38.4], CL: [-71.5, -35.7], CO: [-74.3, 4.6],
  TW: [121.0, 23.7], SG: [103.8, 1.4], HK: [114.1, 22.4],
  ID: [113.9, -0.8], TH: [100.5, 15.9], VN: [108.3, 14.1],
  MY: [101.7, 4.2], PH: [121.8, 12.9], ZA: [22.9, -30.6],
  EG: [30.8, 26.8], NG: [8.7, 9.1], KE: [37.9, -0.0],
  TR: [35.2, 38.9], SA: [45.1, 23.9], AE: [53.8, 23.4],
  IL: [34.9, 31.0], UA: [31.2, 48.4], RO: [24.7, 45.9],
  HU: [19.5, 47.2], GR: [21.8, 39.1], BG: [25.5, 42.7],
  HR: [15.2, 45.1], SK: [19.7, 48.7], LT: [24.0, 55.2],
  LV: [24.6, 56.9], EE: [25.0, 58.6], PE: [-75.0, -9.2],
  VE: [-66.6, 6.4], EC: [-78.2, -1.8], UY: [-55.8, -32.5],
  PY: [-58.4, -23.4], BO: [-63.6, -16.3], CR: [-84.0, 9.7],
  PA: [-80.8, 8.5], DO: [-70.2, 18.7], GT: [-90.2, 15.8],
  CU: [-77.8, 21.5], HN: [-86.2, 15.2], NI: [-85.2, 12.9],
  SV: [-88.9, 13.8], JM: [-77.3, 18.1], TT: [-61.2, 10.7],
  NZ: [174.9, -40.9], PK: [69.3, 30.4], BD: [90.4, 23.7],
  LK: [80.8, 7.9], NP: [84.1, 28.4], MM: [96.0, 21.9],
  KH: [105.0, 12.6], LA: [102.5, 19.9], GE: [43.4, 42.3],
  AZ: [47.6, 40.1], AM: [45.0, 40.1], KZ: [66.9, 48.0],
  UZ: [64.6, 41.4], TM: [58.4, 38.0], KG: [74.8, 41.2],
  TJ: [71.3, 38.9], MA: [-7.1, 31.8], TN: [9.5, 33.9],
  DZ: [1.7, 28.0], LY: [17.2, 26.3], SD: [30.2, 12.9],
  ET: [40.5, 9.1], TZ: [34.9, -6.4], UG: [32.3, 1.4],
  GH: [-1.0, 7.9], SN: [-14.5, 14.5], CI: [-5.5, 7.5],
  CM: [12.4, 7.4], AO: [17.9, -11.2], MZ: [35.5, -18.7],
  ZW: [29.2, -19.0], BW: [24.7, -22.3], NA: [18.5, -22.6],
  MG: [46.9, -18.8], MU: [57.6, -20.3],
  RS: [21.0, 44.0], BA: [17.7, 43.9], AL: [20.2, 41.2],
  MK: [21.7, 41.5], ME: [19.4, 42.7], SI: [14.6, 46.2],
  XK: [20.9, 42.6], MD: [28.4, 47.4], BY: [27.9, 53.7],
  IQ: [43.7, 33.2], IR: [53.7, 32.4], AF: [67.7, 33.9],
  SY: [38.9, 34.8], JO: [36.2, 30.6], LB: [35.9, 33.9],
  KW: [47.5, 29.3], QA: [51.2, 25.4], BH: [50.6, 26.0],
  OM: [55.9, 21.5], YE: [48.5, 15.5],
};

type ModuleKey = "scans" | "cameras" | "ai-sec" | "bd-sec" | "agent-sec" | "host-recon";

interface MapMarker {
  lng: number;
  lat: number;
  label: string;
  module: ModuleKey;
  ip: string;
  port: number;
  details: string;
}

const MODULE_COLORS: Record<ModuleKey, string> = {
  scans: "#c084fc",
  cameras: "#39FF14",
  "ai-sec": "#f5a623",
  "bd-sec": "#ff3030",
  "agent-sec": "#00d4ff",
  "host-recon": "#e0e0e0",
};

const DARK_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  sources: {
    "carto-dark": {
      type: "raster",
      tiles: [
        "https://a.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png",
        "https://b.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png",
        "https://c.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png",
      ],
      tileSize: 256,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/">CARTO</a>',
      maxzoom: 19,
    },
  },
  layers: [{ id: "carto-dark-layer", type: "raster", source: "carto-dark" }],
};

const AI_SEC_SERVICE_GROUPS = [
  {
    label: "Modelos locales / LLM", items: [
      ["ollama", "Ollama"], ["vllm", "vLLM"], ["tgi", "TGI"],
      ["localai", "LocalAI"], ["lmstudio", "LM Studio"],
    ]
  },
  {
    label: "UI / Notebooks", items: [
      ["gradio", "Gradio"], ["ray", "Ray Dashboard"], ["jupyter", "Jupyter"],
      ["openwebui", "Open WebUI"], ["comfyui", "ComfyUI"],
    ]
  },
  {
    label: "Vector DBs", items: [
      ["qdrant", "Qdrant"], ["milvus", "Milvus"],
      ["weaviate", "Weaviate"], ["chroma", "Chroma"],
    ]
  },
] as const;

const WORLD_COUNTRIES = [
  ["", "-- pais --"],
  ["US", "Estados Unidos"], ["CN", "China"], ["DE", "Alemania"],
  ["FR", "Francia"], ["JP", "Japon"], ["KR", "Corea del Sur"],
  ["GB", "Reino Unido"], ["NL", "Paises Bajos"], ["CA", "Canada"],
  ["AU", "Australia"], ["IN", "India"], ["BR", "Brasil"],
  ["MX", "Mexico"], ["ES", "Espana"], ["IT", "Italia"],
  ["SE", "Suecia"], ["RU", "Rusia"], ["SG", "Singapur"],
  ["HK", "Hong Kong"], ["TW", "Taiwan"],
] as const;

const BD_SEC_SERVICE_GROUPS = [
  {
    label: "Bases de datos", items: [
      ["mongodb", "MongoDB"], ["elasticsearch", "Elasticsearch"], ["redis", "Redis"],
      ["mysql", "MySQL"], ["postgresql", "PostgreSQL"], ["couchdb", "CouchDB"],
      ["cassandra", "Cassandra"], ["memcached", "Memcached"],
    ]
  },
  {
    label: "Almacenamiento", items: [
      ["s3", "S3 Buckets"], ["azure_blob", "Azure Blob"],
      ["rsync", "Rsync"], ["ftp", "FTP"],
    ]
  },
  {
    label: "Paneles / Config", items: [
      ["kibana", "Kibana"], ["phpmyadmin", "phpMyAdmin"],
      ["git_exposed", "Git Expuesto"], ["env_file", ".env Expuesto"],
    ]
  },
] as const;

const BD_SEC_COUNTRIES = [
  ["", "-- pais --"],
  ["MX", "Mexico"], ["US", "Estados Unidos"], ["BR", "Brasil"],
  ["AR", "Argentina"], ["CO", "Colombia"], ["CL", "Chile"],
  ["ES", "Espana"], ["DE", "Alemania"], ["FR", "Francia"],
  ["GB", "Reino Unido"], ["NL", "Paises Bajos"], ["RU", "Rusia"],
  ["CN", "China"], ["IN", "India"],
] as const;

const CAM_BRANDS = [
  ["", "-- marca --"],
  ["axis", "Axis"], ["hikvision", "Hikvision"], ["mobotix", "Mobotix"],
  ["webcamxp", "WebcamXP"], ["dahua", "Dahua"], ["foscam", "Foscam"],
  ["panasonic", "Panasonic"], ["sony", "Sony"], ["bosch", "Bosch"],
  ["wansview", "Wansview"], ["tp-link", "TP-Link"], ["grandstream", "Grandstream"],
  ["geovision", "GeoVision"], ["pelco", "Pelco"], ["uniview", "Uniview"],
  ["samsung", "Samsung"], ["vivotek", "Vivotek"], ["arecont", "Arecont"],
  ["amcrest", "Amcrest"], ["reolink", "Reolink"], ["xmeye", "Xmeye"],
  ["ezviz", "EZVIZ"], ["swann", "Swann"], ["arlo", "Arlo"],
] as const;

const CAM_COUNTRIES = [
  ["", "-- pais --"],
  ["country:ES", "Espana"], ["country:FR", "Francia"], ["country:IT", "Italia"],
  ["country:DE", "Alemania"], ["country:US", "Estados Unidos"], ["country:RU", "Rusia"],
  ["country:CN", "China"], ["country:JP", "Japon"], ["country:KR", "Corea del Sur"],
  ["country:CA", "Canada"], ["country:MX", "Mexico"], ["country:BR", "Brasil"],
  ["country:AR", "Argentina"], ["country:CO", "Colombia"], ["country:UK", "Reino Unido"],
  ["country:NL", "Paises Bajos"], ["country:IN", "India"], ["country:AU", "Australia"],
  ["country:TR", "Turquia"], ["country:TH", "Tailandia"],
] as const;

const CAM_DEVICES = [
  ["", "-- tipo --"],
  ["ipcam", "Camara IP"], ["DVR", "DVR"], ["NVR", "NVR"],
  ["cctv", "CCTV"], ["surveillance", "Vigilancia"], ["ptz", "PTZ"],
  ["webcam", "Webcam"], ["baby monitor", "Vigilabebes"],
  ["doorbell", "Videoportero"], ["thermal", "Termica"],
] as const;

const CAM_TECHS = [
  ["", "-- protocolo --"],
  ["RTSP", "RTSP"], ["HTTP", "HTTP"], ["HTTPS", "HTTPS"], ["MJPEG", "MJPEG"],
  ["ONVIF", "ONVIF"], ["H.264", "H.264"], ["H.265", "H.265"],
] as const;

const AGENT_SEC_SERVICES = [
  ["", "-- agente --"],
  ["openclaw", "OpenClaw (Modern)"],
  ["clawdbot", "Clawdbot (Legacy)"],
  ["all", "Todos los agentes conocidos"],
] as const;

const AGENT_SEC_PORTS = [
  ["", "-- puerto (obligatorio) --"],
  ["5353", "5353 · mDNS/UDP"],
  ["443", "443 · Control UI HTTPS"],
  ["18789", "18789 · gateway TCP"],
] as const;

const AGENT_AUTH_LABELS: Record<string, string> = {
  none: "SIN AUTH",
  http_401: "auth (HTTP 401)",
  html_form: "auth (form)",
  ws: "auth (WebSocket)",
  unknown: "desconocida",
};

/* ── Scan configuration ── */

const SCAN_TIERS = [
  { label: "dual-core", apis: ["dns_enum", "whois_rdap"] },
  { label: "tier1 · scanners", apis: ["shodan", "censys", "netlas", "leakix", "crtsh"] },
  { label: "tier2 · vulns", apis: ["circl", "vulners", "nvd"] },
  { label: "tier3 · reputation", apis: ["virustotal", "dnsbl"] },
  { label: "tier4 · network", apis: ["ipinfo", "ssl_tls"] },
  { label: "tier5 · web", apis: ["urlscan", "sec_headers"] },
  { label: "local", apis: ["nmap"] },
];
const ALL_ACTIVE_APIS = SCAN_TIERS.flatMap((t) => t.apis);

const AI_MODELS = [
  { value: "none", label: "none (skip AI)" },
  { value: "llama-3.3-70b", label: "llama-3.3-70b" },
  { value: "llama-3.1-8b", label: "llama-3.1-8b" },
  { value: "deepseek-r1", label: "deepseek-r1" },
  { value: "mistral-small", label: "mistral-small" },
  { value: "qwen-32b", label: "qwen-32b" },
];

const STATUS_MARK: Record<ApiStatus, string> = {
  idle: "·", run: "▸", ok: "✓", error: "✗", skip: "─",
};

function jitter(): number {
  return (Math.random() - 0.5) * 5;
}

function riskClass(s?: number): string {
  if (s == null) return "";
  if (s >= 70) return "red";
  if (s >= 40) return "amber";
  return "";
}

function reconProbeType(port: number): "ollama" | "openclaw" | null {
  if (port === 11434) return "ollama";
  if (port === 18789 || port === 443) return "openclaw";
  return null;
}

export function WorldPane() {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const popupRef = useRef<maplibregl.Popup | null>(null);
  const sourceReady = useRef(false);
  const scanSubRef = useRef<(() => void) | null>(null);
  const apiSelectorRef = useRef<HTMLDivElement>(null);
  const probeCallbackRef = useRef<((ip: string, port: number) => void) | null>(null);

  const [coords, setCoords] = useState({ lat: 20, lng: 0 });
  const [zoom, setZoom] = useState(2.0);
  const [points, setPoints] = useState<MapMarker[]>([]);
  const [visible, setVisible] = useState<Record<ModuleKey, boolean>>({
    scans: true,
    cameras: true,
    "ai-sec": true,
    "bd-sec": true,
    "agent-sec": true,
    "host-recon": true,
  });
  const [busy, setBusy] = useState(false);
  const [module, setModule] = useState<ModuleKey>("scans");
  const [searchEngine, setSearchEngine] = useState("shodan");
  const [query, setQuery] = useState("");
  const [panelOpen, setPanelOpen] = useState(true);
  const [rpanelOpen, setRpanelOpen] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /* ai-sec filters + probe */
  const [aiCountry, setAiCountry] = useState("");
  const [aiCity, setAiCity] = useState("");
  const [aiResults, setAiResults] = useState<AiExposureResult[]>([]);
  const [aiPage, setAiPage] = useState(1);
  const [aiHasMore, setAiHasMore] = useState(false);
  const aiPageTriggerRef = useRef(false);
  const AI_PAGE_SIZE = 50;
  const [probeResults, setProbeResults] = useState<Record<string, ProbeResult>>({});
  const [probing, setProbing] = useState<string | null>(null);
  const [expandedProbe, setExpandedProbe] = useState<string | null>(null);

  /* agent-sec filters + state */
  const [agentCountry, setAgentCountry] = useState("");
  const [agentCity, setAgentCity] = useState("");
  const [agentPort, setAgentPort] = useState("");
  const [agentDeepProbe] = useState(false);
  const [agentResults, setAgentResults] = useState<AgentExposureResult[]>([]);
  const [agentPage, setAgentPage] = useState(1);
  const [agentHasMore, setAgentHasMore] = useState(false);
  const agentPageTriggerRef = useRef(false);
  const AGENT_PAGE_SIZE = 50;

  /* bd-sec filters */
  const [bdCountry, setBdCountry] = useState("");
  const [bdCity, setBdCity] = useState("");
  const [bdResults, setBdResults] = useState<BdSecResult[]>([]);
  const [bdPage, setBdPage] = useState(1);
  const [bdHasMore, setBdHasMore] = useState(false);
  const bdPageTriggerRef = useRef(false);
  const BD_PAGE_SIZE = 50;

  /* host-recon state */
  const [reconIp, setReconIp] = useState("");
  const [reconResults, setReconResults] = useState<AiExposureResult[]>([]);

  /* cameras filters + state */
  const [camBrand, setCamBrand] = useState("");
  const [camCountry, setCamCountry] = useState("");
  const [camCity, setCamCity] = useState("");
  const [camDevice, setCamDevice] = useState("");
  const [camTech, setCamTech] = useState("");
  const [camResults, setCamResults] = useState<CameraResult[]>([]);
  const [bypassing, setBypassing] = useState<string | null>(null);
  const [camPage, setCamPage] = useState(1);
  const [camHasMore, setCamHasMore] = useState(false);
  const camPageTriggerRef = useRef(false);
  const CAM_PAGE_SIZE = 50;

  /* Scan-specific state */
  const [scanTarget, setScanTarget] = useState("");
  const [scanApis, setScanApis] = useState<Set<string>>(new Set(ALL_ACTIVE_APIS));
  const [aiModel, setAiModel] = useState("llama-3.3-70b");
  const [activeScan, setActiveScan] = useState<Scan | null>(null);
  const [apiDropOpen, setApiDropOpen] = useState(false);
  const [openScanApi, setOpenScanApi] = useState<string | null>(null);

  /* ── Keep probe callback ref current (set after executeProbe is defined) ── */

  // Update popup probe zone when probeResults change
  useEffect(() => {
    const popup = popupRef.current;
    if (!popup) return;
    const el = popup.getElement();
    if (!el) return;
    const zone = el.querySelector(".wp-probe-zone");
    if (!zone) return;
    const ipAttr = zone.getAttribute("data-ip");
    const portAttr = zone.getAttribute("data-port");
    const modAttr = zone.getAttribute("data-module");
    if (!ipAttr || !portAttr) return;
    const key = `${ipAttr}:${portAttr}`;
    const probe = probeResults[key];
    if (!probe) return;

    const sc = (() => {
      const [n, d] = probe.score.split("/").map(Number);
      if (n === d) return "#39FF14";
      if (n > 0) return "#ff9500";
      return "#ff4444";
    })();

    let html = `<span class="wp-probe-score" style="color:${sc};border-color:${sc}">${probe.score}</span>`;

    if (modAttr === "agent-sec") {
      html += `<div class="wp-probe-line"><span style="color:${probe.service_confirmed ? "#39FF14" : "#ff4444"}">${probe.service_confirmed ? "✔" : "✗"}</span> service ${probe.service_confirmed ? "confirmado" : "no confirmado"}${probe.title ? ` · ${probe.title}` : ""}</div>`;
      const ver = probe.version
        ? (typeof probe.version === "object" && "version" in probe.version ? String((probe.version as Record<string, unknown>).version) : JSON.stringify(probe.version))
        : null;
      html += `<div class="wp-probe-line"><span style="color:${probe.version ? "#39FF14" : "#ff4444"}">${probe.version ? "✔" : "✗"}</span> version ${ver ?? "unknown"}</div>`;
      const authLabel = AGENT_AUTH_LABELS[probe.auth_type ?? "unknown"] ?? probe.auth_type;
      const authColor = probe.auth_type === "none" ? "#ff4444" : probe.auth_type === "unknown" ? "#ff9500" : "#39FF14";
      html += `<div class="wp-probe-line"><span style="color:${authColor}">●</span> auth <span style="color:${authColor}">${authLabel}</span></div>`;
      if (probe.http_status) html += `<div class="wp-probe-line"><span class="dim">·</span> http ${probe.http_status}${probe.server_header ? ` · ${probe.server_header}` : ""}</div>`;
      if (probe.cve_matches?.length) html += `<div class="wp-probe-line"><span style="color:#ff4444">!</span> cves <span style="color:#ff4444">${probe.cve_matches.join(" · ")}</span></div>`;
      html += `<div class="wp-probe-line dim" style="font-size:10px">risk_score <b style="color:#ff4444">${probe.risk_score ?? "?"}</b>/10</div>`;
    } else {
      if (probe.version) {
        const ver = typeof probe.version === "object" && "version" in probe.version
          ? String((probe.version as Record<string, unknown>).version)
          : JSON.stringify(probe.version);
        html += `<div class="wp-probe-line"><span style="color:#39FF14">✔</span> version ${ver}</div>`;
      } else {
        html += `<div class="wp-probe-line"><span style="color:#ff4444">✗</span> version no response</div>`;
      }
      if (probe.tags) {
        html += `<div class="wp-probe-line"><span style="color:#39FF14">✔</span> tags ${probe.models_count} modelo${probe.models_count !== 1 ? "s" : ""}</div>`;
        if (probe.model_names?.length) {
          html += probe.model_names.map((n) => `<div class="wp-probe-model">· ${n}</div>`).join("");
        }
      } else {
        html += `<div class="wp-probe-line"><span style="color:#ff4444">✗</span> tags no response</div>`;
      }
    }

    zone.innerHTML = html;
  }, [probeResults]);

  /* ── Map init ── */

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const m = new maplibregl.Map({
      container: containerRef.current,
      style: DARK_STYLE,
      center: [0, 20],
      zoom: 2,
      attributionControl: false,
      minZoom: 1,
      maxZoom: 18,
    });

    m.addControl(
      new maplibregl.NavigationControl({ showCompass: true, visualizePitch: false }),
      "bottom-right",
    );

    m.on("mousemove", (e) => {
      setCoords({ lat: e.lngLat.lat, lng: e.lngLat.lng });
    });

    m.on("zoom", () => setZoom(m.getZoom()));

    m.on("load", () => {
      setZoom(m.getZoom());
      m.addSource("markers", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });

      m.addLayer({
        id: "markers-glow",
        type: "circle",
        source: "markers",
        paint: {
          "circle-radius": 12,
          "circle-color": ["get", "color"],
          "circle-opacity": 0.15,
          "circle-blur": 1,
        },
      });

      m.addLayer({
        id: "markers-ring",
        type: "circle",
        source: "markers",
        filter: ["==", ["get", "module"], "scans"],
        paint: {
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 1, 16, 2, 18, 8, 28, 14, 40],
          "circle-color": "transparent",
          "circle-opacity": 1,
          "circle-stroke-width": 2,
          "circle-stroke-color": "#c084fc",
          "circle-stroke-opacity": 0.4,
        },
      });

      m.addLayer({
        id: "markers-circle",
        type: "circle",
        source: "markers",
        paint: {
          "circle-radius": 5,
          "circle-color": ["get", "color"],
          "circle-opacity": 0.9,
          "circle-stroke-width": 1.5,
          "circle-stroke-color": ["get", "color"],
          "circle-stroke-opacity": 0.4,
        },
      });

      m.on("click", "markers-circle", (e) => {
        if (!e.features?.length) return;
        const f = e.features[0];
        const c = (f.geometry as GeoJSON.Point).coordinates as [number, number];
        const p = f.properties!;

        popupRef.current?.remove();

        const hasProbe = p.module === "ai-sec" || (p.module === "agent-sec" && Number(p.port) !== 5353);
        let probeHtml = "";
        if (hasProbe) {
          probeHtml = `<div class="wp-probe-zone" data-ip="${p.ip}" data-port="${p.port}" data-module="${p.module}">` +
            `<button class="wp-probe-btn">▸ probe</button>` +
            `</div>`;
        }

        popupRef.current = new maplibregl.Popup({
          closeButton: true,
          closeOnClick: true,
          className: "world-popup",
          maxWidth: "340px",
        })
          .setLngLat(c)
          .setHTML(
            `<div class="wp-inner">` +
            `<div class="wp-mod" style="color:${p.color}">[${p.module}]</div>` +
            `<div class="wp-ip">${p.ip}:${p.port}</div>` +
            `<div class="wp-label">${p.label}</div>` +
            `<div class="wp-detail">${p.details}</div>` +
            probeHtml +
            `</div>`,
          )
          .addTo(m);

        if (hasProbe) {
          const btn = popupRef.current.getElement()?.querySelector(".wp-probe-btn");
          btn?.addEventListener("click", () => {
            probeCallbackRef.current?.(p.ip as string, Number(p.port));
          });
        }
      });

      m.on("mouseenter", "markers-circle", () => {
        m.getCanvas().style.cursor = "crosshair";
      });
      m.on("mouseleave", "markers-circle", () => {
        m.getCanvas().style.cursor = "";
      });

      sourceReady.current = true;
    });

    mapRef.current = m;

    return () => {
      popupRef.current?.remove();
      m.remove();
      mapRef.current = null;
      sourceReady.current = false;
      scanSubRef.current?.();
      scanSubRef.current = null;
    };
  }, []);

  /* ── Marker sync ── */

  useEffect(() => {
    const m = mapRef.current;
    if (!m || !sourceReady.current) return;

    const src = m.getSource("markers") as maplibregl.GeoJSONSource | undefined;
    if (!src) return;

    const features: GeoJSON.Feature[] = points
      .filter((p) => visible[p.module])
      .map((p) => ({
        type: "Feature" as const,
        geometry: { type: "Point" as const, coordinates: [p.lng, p.lat] },
        properties: {
          ip: p.ip,
          port: p.port,
          label: p.label,
          module: p.module,
          details: p.details,
          color: MODULE_COLORS[p.module],
        },
      }));

    src.setData({ type: "FeatureCollection", features });
  }, [points, visible]);

  /* ── API dropdown close-on-click-outside ── */

  useEffect(() => {
    function close(e: MouseEvent) {
      if (apiSelectorRef.current && !apiSelectorRef.current.contains(e.target as Node))
        setApiDropOpen(false);
    }
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  /* ── Module search (cameras/ai-sec/bd-sec/agent-sec) ── */

  const camQuery = (() => {
    const cityPart = camCity.trim() ? `city:"${camCity.trim().replace(/"/g, "")}"` : "";
    return [camBrand, camTech, camCountry, cityPart, camDevice].filter(Boolean).join(" ");
  })();

  const handleSearch = useCallback(async () => {
    if (module === "host-recon") {
      if (!reconIp.trim()) return;
    } else if (module === "cameras" ? !camQuery : !query.trim()) {
      return;
    }
    setBusy(true);
    setError(null);

    try {
      let newPoints: MapMarker[] = [];

      if (module === "cameras") {
        const resp = await searchCameras({
          query: camQuery,
          limit: CAM_PAGE_SIZE,
          page: camPage,
          engine: searchEngine,
        });
        setCamResults(resp.results);
        // Si el backend devolvio menos que el tamaño de pagina, no hay mas paginas
        setCamHasMore(resp.results.length >= CAM_PAGE_SIZE);
        newPoints = resp.results
          .filter((r) => r.lat != null && r.lon != null)
          .map((r) => ({
            lng: r.lon!,
            lat: r.lat!,
            label: `${r.brand || r.product || "cam"} · ${r.org}`,
            module: "cameras" as const,
            ip: r.ip,
            port: r.port,
            details: `${r.country} · ${r.city}${r.accessible ? " · accessible" : ""}${r.auth_required ? " · auth" : ""}`,
          }));
      } else if (module === "ai-sec") {
        const resp = await searchAiExposure({
          service: query,
          country: aiCountry || undefined,
          city: aiCity.trim() || undefined,
          limit: AI_PAGE_SIZE,
          page: aiPage,
          engine: searchEngine,
        });
        setAiResults(resp.results);
        setAiHasMore(resp.results.length >= AI_PAGE_SIZE);
        setProbeResults({});
        setExpandedProbe(null);
        newPoints = resp.results
          .filter((r) => (r.lat != null && r.lon != null) || (r.countryCode && COUNTRY_CENTROIDS[r.countryCode]))
          .map((r) => {
            const hasReal = r.lat != null && r.lon != null;
            const centroid = COUNTRY_CENTROIDS[r.countryCode];
            const lng = hasReal ? r.lon! : centroid[0] + jitter();
            const lat = hasReal ? r.lat! : centroid[1] + jitter();
            return {
              lng,
              lat,
              label: `${r.service} ${r.version} · ${r.org}`,
              module: "ai-sec" as const,
              ip: r.ip,
              port: r.port,
              details: `${r.country} · ${r.city} · ${r.riskLevel}`,
            };
          });
      } else if (module === "bd-sec") {
        const resp = await searchBdSec({
          service: query,
          country: bdCountry || undefined,
          city: bdCity.trim() || undefined,
          limit: BD_PAGE_SIZE,
          page: bdPage,
          engine: searchEngine,
        });
        setBdResults(resp.results);
        setBdHasMore(resp.stats.page < resp.stats.total_pages);
        newPoints = resp.results
          .filter((r) => (r.lat != null && r.lon != null) || COUNTRY_CENTROIDS[r.countryCode])
          .map((r) => {
            const hasReal = r.lat != null && r.lon != null;
            const centroid = COUNTRY_CENTROIDS[r.countryCode];
            const lng = hasReal ? r.lon! : centroid[0] + jitter();
            const lat = hasReal ? r.lat! : centroid[1] + jitter();
            return {
              lng,
              lat,
              label: `${r.service} ${r.version} · ${r.org}`,
              module: "bd-sec" as const,
              ip: r.ip,
              port: r.port,
              details: `${r.country} · ${r.city} · ${r.riskLevel} · ${r.dataSize}`,
            };
          });
      } else if (module === "agent-sec") {
        const resp = await searchAgentExposure({
          service: query,
          country: agentCountry || undefined,
          city: agentCity.trim() || undefined,
          port: agentPort ? (Number(agentPort) as number) : undefined,
          limit: AGENT_PAGE_SIZE,
          page: agentPage,
          engine: searchEngine,
        });
        setAgentResults(resp.results);
        setAgentHasMore(resp.stats.page < resp.stats.total_pages);
        setProbeResults({});
        setExpandedProbe(null);
        newPoints = resp.results
          .filter((r) => (r.lat != null && r.lon != null) || COUNTRY_CENTROIDS[r.countryCode])
          .map((r) => {
            const hasReal = r.lat != null && r.lon != null;
            const centroid = COUNTRY_CENTROIDS[r.countryCode];
            const lng = hasReal ? r.lon! : centroid[0] + jitter();
            const lat = hasReal ? r.lat! : centroid[1] + jitter();
            return {
              lng,
              lat,
              label: `${r.serviceLabel} ${r.version} · ${r.org}`,
              module: "agent-sec" as const,
              ip: r.ip,
              port: r.port,
              details: `${r.country} · ${r.city} · ${r.riskLevel}`,
            };
          });
      } else if (module === "host-recon") {
        const resp = await lookupAiExposureIp({
          ip: reconIp.trim(),
          engine: searchEngine,
        });
        setReconResults(resp.results);
        setProbeResults({});
        setExpandedProbe(null);

        // host-recon es lookup de UNA IP: cada motor devuelve N registros (uno
        // por puerto/snapshot) que comparten el mismo pais. Colapsamos a UN
        // marker por IP unica para no dispersar 1000 puntos sobre el centroide.
        // El panel derecho sigue mostrando el detalle de todos los puertos.
        const byIp = new Map<string, AiExposureResult[]>();
        for (const r of resp.results) {
          const hasReal = r.lat != null && r.lon != null;
          const hasCentroid = r.countryCode && COUNTRY_CENTROIDS[r.countryCode];
          if (!hasReal && !hasCentroid) continue;
          const arr = byIp.get(r.ip);
          if (arr) arr.push(r);
          else byIp.set(r.ip, [r]);
        }

        newPoints = [...byIp.entries()].map(([ip, recs]) => {
          const first = recs[0];
          const withCoords = recs.find((r) => r.lat != null && r.lon != null);
          const hasReal = withCoords != null;
          const centroid = COUNTRY_CENTROIDS[first.countryCode];
          const lng = hasReal ? withCoords!.lon! : centroid[0] + jitter();
          const lat = hasReal ? withCoords!.lat! : centroid[1] + jitter();
          const ports = [...new Set(recs.map((r) => r.port))].sort((a, b) => a - b);
          const portsLabel = ports.length > 8
            ? `${ports.slice(0, 8).join(", ")} +${ports.length - 8}`
            : ports.join(", ");
          return {
            lng,
            lat,
            label: `${ip} · ${first.org}`,
            module: "host-recon" as const,
            ip,
            port: ports[0] ?? 0,
            details: `${first.country} · ${first.city} · ${ports.length} puerto${ports.length !== 1 ? "s" : ""}: ${portsLabel}`,
          };
        });
      }

      setPoints((prev) => [...prev.filter((p) => p.module !== module), ...newPoints]);

      if (newPoints.length > 0 && mapRef.current) {
        const bounds = new maplibregl.LngLatBounds();
        newPoints.forEach((p) => bounds.extend([p.lng, p.lat]));
        mapRef.current.fitBounds(bounds, { padding: 80, maxZoom: 8, duration: 1200 });
      }

      if (newPoints.length === 0) {
        setError("0 results with coordinates");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "search failed");
    } finally {
      setBusy(false);
    }
  }, [module, query, camQuery, aiCountry, aiCity, reconIp, agentCountry, agentCity, agentPort, bdCountry, bdCity, searchEngine, camPage, aiPage, bdPage, agentPage]);

  /* ── Cameras pagination — dispara search solo cuando el user hace click en prev/next ── */
  useEffect(() => {
    if (!camPageTriggerRef.current) return;
    camPageTriggerRef.current = false;
    handleSearch();
  }, [camPage, handleSearch]);

  /* ── Agent-sec pagination ── */
  useEffect(() => {
    if (!agentPageTriggerRef.current) return;
    agentPageTriggerRef.current = false;
    handleSearch();
  }, [agentPage, handleSearch]);
  useEffect(() => {
    if (!bdPageTriggerRef.current) return;
    bdPageTriggerRef.current = false;
    handleSearch();
  }, [bdPage, handleSearch]);
  useEffect(() => {
    if (!aiPageTriggerRef.current) return;
    aiPageTriggerRef.current = false;
    handleSearch();
  }, [aiPage, handleSearch]);

  /* ── Scan launch ── */

  const handleScanLaunch = useCallback(async () => {
    if (!scanTarget.trim() || scanApis.size === 0) return;
    setBusy(true);
    setError(null);
    setOpenScanApi(null);

    try {
      const apisStr =
        scanApis.size === ALL_ACTIVE_APIS.length
          ? "full"
          : Array.from(scanApis).join(",");

      const resp = await startScan({ target: scanTarget, apis: apisStr, aiModel });
      const scan: Scan = {
        scanId: resp.scanId,
        targetId: resp.targetId,
        targetRaw: scanTarget,
        action: apisStr === "full" ? "full" : "discover",
        startedAt: resp.startedAt,
        state: "running",
        apis: API_CATALOG.map((a) => ({ ...a })),
        report: null,
      };

      setActiveScan(scan);

      scanSubRef.current?.();
      const stop = subscribeScan(scan, (updated) => {
        setActiveScan(updated);

        if (updated.state !== "running") {
          let lat: number | undefined;
          let lng: number | undefined;

          for (const api of updated.apis) {
            if (api.status !== "ok" || !api.payload) continue;
            const p = api.payload as Record<string, unknown>;
            if (typeof p.lat === "number" && typeof p.lon === "number") {
              lat = p.lat; lng = p.lon; break;
            }
            if (typeof p.latitude === "number" && typeof p.longitude === "number") {
              lat = p.latitude; lng = p.longitude; break;
            }
            if (typeof p.loc === "string" && p.loc.includes(",")) {
              const parts = p.loc.split(",").map(Number);
              if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
                lat = parts[0]; lng = parts[1]; break;
              }
            }
            const cc = (p.countryCode || p.country_code) as string | undefined;
            if (!lat && cc && COUNTRY_CENTROIDS[cc]) {
              [lng, lat] = COUNTRY_CENTROIDS[cc];
            }
          }

          if (lat != null && lng != null) {
            const marker: MapMarker = {
              lat, lng,
              label: `scan · ${updated.targetRaw}`,
              module: "scans",
              ip: updated.targetRaw,
              port: 0,
              details: `${updated.state} · risk ${updated.report?.risk_score ?? "?"}/100`,
            };
            setPoints((prev) => [...prev.filter((p) => p.module !== "scans"), marker]);
          }
        }
      });

      scanSubRef.current = stop;
    } catch (err) {
      setError(err instanceof Error ? err.message : "scan failed");
    } finally {
      setBusy(false);
    }
  }, [scanTarget, scanApis, aiModel]);

  /* ── Scan API toggle helpers ── */

  const toggleScanApi = useCallback((api: string) => {
    setScanApis((prev) => {
      const next = new Set(prev);
      next.has(api) ? next.delete(api) : next.add(api);
      return next;
    });
  }, []);

  const toggleAllScanApis = useCallback(() => {
    setScanApis((prev) =>
      prev.size === ALL_ACTIVE_APIS.length ? new Set() : new Set(ALL_ACTIVE_APIS),
    );
  }, []);

  const scanApisLabel = (): string => {
    if (scanApis.size === ALL_ACTIVE_APIS.length) return "full";
    if (scanApis.size === 0) return "none";
    if (scanApis.size <= 2) return Array.from(scanApis).join(",");
    return `${scanApis.size} apis`;
  };

  /* ── ai-sec probe ── */

  const executeProbe = useCallback(async (ip: string, port: number, probeType?: string) => {
    const key = `${ip}:${port}`;
    setProbing(key);
    const popup = popupRef.current;
    const btn = popup?.getElement()?.querySelector(".wp-probe-btn") as HTMLButtonElement | null;
    if (btn) {
      btn.textContent = "probing...";
      btn.disabled = true;
    }
    try {
      const pt = (probeType as "ollama" | "openclaw") ?? "ollama";
      const result = await probeTarget({
        ip, port, probeType: pt,
        ...(pt === "openclaw" && agentDeepProbe ? { deepProbe: true } : {}),
      });
      setProbeResults((prev) => ({ ...prev, [key]: result }));
    } catch {
      setProbeResults((prev) => ({
        ...prev,
        [key]: {
          ip, port, score: "0/2", max_score: 2,
          version: null, tags: null,
          models_count: 0, model_names: [],
          probed_at: new Date().toISOString(),
        },
      }));
    } finally {
      setProbing(null);
    }
  }, [agentDeepProbe]);

  useEffect(() => {
    probeCallbackRef.current = (ip: string, port: number) => {
      const aiR = aiResults.find((a) => a.ip === ip && a.port === port);
      if (aiR) { executeProbe(ip, port, aiR.probeType); return; }
      const agR = agentResults.find((a) => a.ip === ip && a.port === port);
      if (agR) { executeProbe(ip, port, agR.probeType ?? "openclaw"); return; }
      const reconR = reconResults.find((r) => r.ip === ip && r.port === port);
      if (reconR) {
        const pt = reconProbeType(reconR.port);
        if (pt) executeProbe(ip, port, pt);
        return;
      }
      executeProbe(ip, port, "openclaw");
    };
  }, [aiResults, agentResults, reconResults, executeProbe]);

  /* ── Camera bypass ── */

  const tryBypass = useCallback(async (r: CameraResult) => {
    if (!r.brand) return;
    const key = `${r.ip}:${r.port}`;
    setBypassing(key);
    try {
      const resp = await checkBypass({ ip: r.ip, port: r.port, brand: r.brand });
      setCamResults((prev) =>
        prev.map((x) =>
          x.ip === r.ip && x.port === r.port
            ? { ...x, bypass_url: resp.success ? resp.url : "__nope__" }
            : x,
        ),
      );
    } finally {
      setBypassing(null);
    }
  }, []);

  /* ── Clear helpers ── */

  const clearModule = useCallback(
    (m: ModuleKey) => {
      setPoints((prev) => prev.filter((p) => p.module !== m));
      if (m === "scans") setActiveScan(null);
    },
    [],
  );

  const clearAll = useCallback(() => {
    setPoints([]);
    setActiveScan(null);
  }, []);

  /* ── Derived ── */

  const counts: Record<ModuleKey, number> = {
    scans: activeScan ? 1 : 0,
    cameras: points.filter((p) => p.module === "cameras").length,
    "ai-sec": points.filter((p) => p.module === "ai-sec").length,
    "bd-sec": points.filter((p) => p.module === "bd-sec").length,
    "agent-sec": points.filter((p) => p.module === "agent-sec").length,
    "host-recon": points.filter((p) => p.module === "host-recon").length,
  };
  const totalVisible = points.filter((p) => visible[p.module]).length;

  const hasResults =
    camResults.length > 0 ||
    aiResults.length > 0 ||
    agentResults.length > 0 ||
    bdResults.length > 0 ||
    reconResults.length > 0 ||
    totalVisible > 0;

  const readyForDownload = useMemo(() => {
    if (!activeScan) return false;
    if (activeScan.state !== "running") return true;
    const nmap = activeScan.apis.find((a) => a.name === "nmap");
    return !!nmap && nmap.status !== "idle" && nmap.status !== "run";
  }, [activeScan]);

  const downloadRawJson = useCallback(() => {
    if (!activeScan) return;
    const { report: _report, ...rest } = activeScan;
    const body = JSON.stringify(rest, null, 2);
    const safe = activeScan.targetRaw.replace(/[^a-zA-Z0-9._-]/g, "_");
    const filename = `shadowargus_${safe}_${activeScan.scanId.slice(0, 8)}.json`;
    const blob = new Blob([body], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [activeScan]);

  return (
    <div className="world-container">
      <div ref={containerRef} className="world-map" />
      <div className="world-scanlines" />
      <div className="world-vignette" />

      {/* ── Top HUD ── */}
      <div className="world-hud-top">
        <span className="world-hud-title">
          <span className="glyph">┌──</span> world · global threat map{" "}
          <span className="glyph">──────────────────────────┐</span>
        </span>
      </div>

      {/* ── Side Panel ── */}
      <div
        className={`world-panel ${panelOpen ? "" : "world-panel-collapsed"}`}
      >
        <button
          className="world-panel-toggle"
          onClick={() => setPanelOpen((p) => !p)}
          title={panelOpen ? "collapse" : "expand"}
        >
          {panelOpen ? "◂ hide" : "▸"}
        </button>

        {panelOpen && (
          <>
            {/* ── Layers ── */}
            <div className="world-section">
              <div className="world-section-label">── layers ──</div>
              {(Object.keys(visible) as ModuleKey[]).map((m) => (
                <label key={m} className="world-layer">
                  <span className="world-dot" style={{ background: MODULE_COLORS[m] }} />
                  <input
                    type="checkbox"
                    checked={visible[m]}
                    onChange={() => setVisible((v) => ({ ...v, [m]: !v[m] }))}
                  />
                  <span className="world-layer-name">{m}</span>
                  <span className="world-layer-ct">{counts[m] || ""}</span>
                  {counts[m] > 0 && (
                    <button
                      className="world-layer-clear"
                      onClick={(e) => { e.preventDefault(); clearModule(m); }}
                      title="clear"
                    >
                      x
                    </button>
                  )}
                </label>
              ))}
              {points.length > 0 && (
                <button className="btn-sm world-clear-all" onClick={clearAll}>
                  [ clear all ]
                </button>
              )}
            </div>

            {/* ── Search ── */}
            <div className="world-section">
              <div className="world-section-label">── search ──</div>
              <select
                className="cam-select world-select"
                value={module}
                onChange={(e) => {
                  const nextModule = e.target.value as ModuleKey;
                  setModule(nextModule);
                  // cámaras, ai-sec, bd-sec y agent-sec tienen Censys global-search bloqueado
                  if (searchEngine === "censys" && (nextModule === "cameras" || nextModule === "ai-sec" || nextModule === "bd-sec" || nextModule === "agent-sec")) {
                    setSearchEngine("shodan");
                  } else if (searchEngine === "leakix" && nextModule !== "bd-sec") {
                    setSearchEngine("shodan");
                  }
                  setQuery("");
                  setError(null);
                  setApiDropOpen(false);
                }}
              >
                <option value="scans">scans</option>
                <option value="cameras">cameras</option>
                <option value="ai-sec">ai-sec</option>
                <option value="bd-sec">bd-sec</option>
                <option value="agent-sec">agent-sec</option>
                <option value="host-recon">host-recon</option>
              </select>

              {module !== "scans" && (
                <select
                  className="cam-select world-select"
                  value={searchEngine}
                  onChange={(e) => {
                    setSearchEngine(e.target.value);
                  }}
                  style={{ color: searchEngine === "netlas" ? "#39FF14" : searchEngine === "censys" ? "#00ffff" : "#ff4444" }}
                >
                  <option value="netlas">-- motor: Netlas --</option>
                  <option value="shodan">-- motor: Shodan --</option>
                  {module === "bd-sec" && (
                    <option value="leakix">-- motor: LeakIX --</option>
                  )}
                  {/* Censys Free tier bloquea search global. */}
                  {module !== "cameras" && module !== "ai-sec" && module !== "bd-sec" && module !== "agent-sec" && (
                    <option value="censys">-- motor: Censys --</option>
                  )}
                </select>
              )}

              {module === "scans" ? (
                <>
                  <input
                    className="cam-input world-input"
                    placeholder="ip o dominio..."
                    value={scanTarget}
                    onChange={(e) => setScanTarget(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleScanLaunch()}
                  />
                  <div className="world-scan-controls">
                    <button
                      type="button"
                      className="btn-sm world-api-btn"
                      onClick={() => setApiDropOpen((o) => !o)}
                    >
                      apis: {scanApisLabel()} {apiDropOpen ? "▴" : "▾"}
                    </button>
                    <select
                      className="cam-select world-model-select"
                      value={aiModel}
                      onChange={(e) => setAiModel(e.target.value)}
                    >
                      {AI_MODELS.map((m) => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                  </div>
                  {apiDropOpen && (
                    <div className="world-api-inline" ref={apiSelectorRef}>
                      <label
                        className="world-api-opt world-api-full"
                        onClick={toggleAllScanApis}
                      >
                        <span>
                          {scanApis.size === ALL_ACTIVE_APIS.length ? "☑" : "☐"}
                        </span>
                        full (todas)
                      </label>
                      {SCAN_TIERS.map((tier) => (
                        <div key={tier.label}>
                          <div className="world-api-tier-label">{tier.label}</div>
                          {tier.apis.map((api) => (
                            <label
                              key={api}
                              className="world-api-opt"
                              onClick={() => toggleScanApi(api)}
                            >
                              <span>{scanApis.has(api) ? "☑" : "☐"}</span>
                              {api}
                            </label>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    className="btn world-scan-btn"
                    onClick={handleScanLaunch}
                    disabled={busy || !scanTarget.trim() || scanApis.size === 0}
                  >
                    {busy ? "launching..." : "[ exec ]"}
                  </button>
                </>
              ) : module === "cameras" ? (
                <>
                  <select className="cam-select world-select" value={camBrand} onChange={(e) => { setCamBrand(e.target.value); setCamPage(1); }}>
                    {CAM_BRANDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  <select className="cam-select world-select" value={camCountry} onChange={(e) => { setCamCountry(e.target.value); setCamPage(1); }}>
                    {CAM_COUNTRIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  <input
                    className="cam-input world-input"
                    placeholder="ciudad (ej. Monterrey)"
                    value={camCity}
                    onChange={(e) => { setCamCity(e.target.value); setCamPage(1); }}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  />
                  <select className="cam-select world-select" value={camDevice} onChange={(e) => { setCamDevice(e.target.value); setCamPage(1); }}>
                    {CAM_DEVICES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  <select className="cam-select world-select" value={camTech} onChange={(e) => { setCamTech(e.target.value); setCamPage(1); }}>
                    {CAM_TECHS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  <div className="world-cam-query dim">
                    query · <span className="amber">{camQuery || "(selecciona filtros)"}</span>
                  </div>
                  <button
                    className="btn world-scan-btn"
                    onClick={handleSearch}
                    disabled={busy || !camQuery}
                  >
                    {busy ? "scanning..." : "[ scan ]"}
                  </button>
                </>
              ) : module === "ai-sec" ? (
                <>
                  <select
                    className="cam-select world-select"
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); setAiPage(1); }}
                  >
                    <option value="">-- servicio --</option>
                    {AI_SEC_SERVICE_GROUPS.map((g) => (
                      <optgroup key={g.label} label={g.label}>
                        {g.items.map(([v, label]) => (
                          <option key={v} value={v}>{label}</option>
                        ))}
                      </optgroup>
                    ))}
                    <option value="all">Todos (puertos unicos)</option>
                  </select>
                  <select
                    className="cam-select world-select"
                    value={aiCountry}
                    onChange={(e) => { setAiCountry(e.target.value); setAiPage(1); }}
                  >
                    {WORLD_COUNTRIES.map(([v, label]) => (
                      <option key={v} value={v}>{label}</option>
                    ))}
                  </select>
                  <input
                    className="cam-input world-input"
                    placeholder="ciudad (ej. Monterrey)"
                    value={aiCity}
                    onChange={(e) => { setAiCity(e.target.value); setAiPage(1); }}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  />
                  <button
                    className="btn world-scan-btn"
                    onClick={handleSearch}
                    disabled={busy || !query.trim()}
                  >
                    {busy ? "scanning..." : "[ scan ]"}
                  </button>
                </>
              ) : module === "host-recon" ? (
                <>
                  <input
                    className="cam-input world-input"
                    placeholder="IP (ej. 45.33.32.156)"
                    value={reconIp}
                    onChange={(e) => setReconIp(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    spellCheck={false}
                    autoComplete="off"
                  />
                  <button
                    className="btn world-scan-btn"
                    onClick={handleSearch}
                    disabled={busy || !reconIp.trim()}
                  >
                    {busy ? "scanning..." : "[ lookup ]"}
                  </button>
                </>
              ) : module === "bd-sec" ? (
                <>
                  <select
                    className="cam-select world-select"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  >
                    <option value="">-- servicio --</option>
                    {BD_SEC_SERVICE_GROUPS.map((g) => (
                      <optgroup key={g.label} label={g.label}>
                        {g.items.map(([v, label]) => (
                          <option key={v} value={v}>{label}</option>
                        ))}
                      </optgroup>
                    ))}
                    <option value="all">Todos</option>
                  </select>
                  <select
                    className="cam-select world-select"
                    value={bdCountry}
                    onChange={(e) => setBdCountry(e.target.value)}
                  >
                    {BD_SEC_COUNTRIES.map(([v, label]) => (
                      <option key={v} value={v}>{label}</option>
                    ))}
                  </select>
                  <input
                    className="cam-input world-input"
                    placeholder="ciudad (ej. Monterrey)"
                    value={bdCity}
                    onChange={(e) => setBdCity(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  />
                  <button
                    className="btn world-scan-btn"
                    onClick={handleSearch}
                    disabled={busy || !query.trim()}
                  >
                    {busy ? "scanning..." : "[ scan ]"}
                  </button>
                </>
              ) : (
                <>
                  <select
                    className="cam-select world-select"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  >
                    {AGENT_SEC_SERVICES.map(([v, label]) => (
                      <option key={v} value={v}>{label}</option>
                    ))}
                  </select>
                  <select
                    className="cam-select world-select"
                    value={agentPort}
                    onChange={(e) => setAgentPort(e.target.value)}
                  >
                    {AGENT_SEC_PORTS.map(([v, label]) => (
                      <option key={v} value={v}>{label}</option>
                    ))}
                  </select>
                  <select
                    className="cam-select world-select"
                    value={agentCountry}
                    onChange={(e) => setAgentCountry(e.target.value)}
                  >
                    {WORLD_COUNTRIES.map(([v, label]) => (
                      <option key={v} value={v}>{label}</option>
                    ))}
                  </select>
                  <input
                    className="cam-input world-input"
                    placeholder="ciudad (ej. Monterrey)"
                    value={agentCity}
                    onChange={(e) => setAgentCity(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  />

                  <button
                    className="btn world-scan-btn"
                    onClick={handleSearch}
                    disabled={busy || !query.trim() || !agentPort}
                  >
                    {busy ? "scanning..." : "[ scan ]"}
                  </button>
                </>
              )}

              {error && <div className="world-error">{error}</div>}
            </div>
          </>
        )}
      </div>

      {/* ── Right Panel (results / reports / probe) ── */}
      {(hasResults || (activeScan && module === "scans")) && (
        <div className={`world-rpanel ${rpanelOpen ? "" : "world-rpanel-collapsed"}`}>
          <button
            className="world-rpanel-toggle"
            onClick={() => setRpanelOpen((p) => !p)}
            title={rpanelOpen ? "collapse" : "expand"}
          >
            {rpanelOpen ? "◂ hide" : "▸"}
          </button>
          {rpanelOpen && <>
          {/* ── Scan Cascade + Report ── */}
          {activeScan && module === "scans" && (
            <div className="world-section">
              <div className="world-section-label">── cascade ──</div>
              <div className="world-scan-info">
                <span className="dim">target</span> {activeScan.targetRaw}
              </div>
              <div className="world-scan-info">
                <span className="dim">state</span>{" "}
                <span className={activeScan.state === "running" ? "amber" : activeScan.state === "error" ? "red" : ""}>
                  {activeScan.state}
                  {activeScan.state === "running" && <span className="caret" />}
                </span>
              </div>
              <div className="world-scan-info">
                <span className="dim">scan_id</span>{" "}
                <span className="ghost">{activeScan.scanId.slice(0, 12)}…</span>
              </div>
              <button
                className="btn-sm dl-json"
                disabled={!readyForDownload}
                onClick={downloadRawJson}
                title={
                  readyForDownload
                    ? "descargar json crudo de todas las apis (sin consolidado llama)"
                    : "aguarda a que termine nmap"
                }
              >
                [ download json ]
              </button>

              <div className="world-cascade">
                {TIER_ORDER.map((tier) => {
                  const tierApis = activeScan.apis.filter((a) => a.tier === tier);
                  if (!tierApis.length) return null;
                  return (
                    <div key={tier} className="world-tier">
                      <div className="world-tier-label">{TIER_LABEL[tier]}</div>
                      {tierApis.map((a) => {
                        const hasPayload = a.payload != null && a.status === "ok";
                        const isOpen = openScanApi === a.name;
                        return (
                          <div key={a.name}>
                            <div
                              className={`world-capi ${a.status}`}
                              onClick={() => hasPayload && setOpenScanApi((cur) => cur === a.name ? null : a.name)}
                              style={{ cursor: hasPayload ? "pointer" : "default" }}
                            >
                              <span className="world-capi-mark">{STATUS_MARK[a.status]}</span>
                              <span className="world-capi-name">
                                {a.name}
                                {hasPayload && <span className="dim"> {isOpen ? "▾" : "▸"}</span>}
                              </span>
                              <span className="world-capi-ms">
                                {a.ms != null ? `${a.ms}ms` : ""}
                              </span>
                            </div>
                            {isOpen && hasPayload && (
                              <pre className="world-capi-payload">
                                {JSON.stringify(a.payload, null, 2)}
                              </pre>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>

              {/* ── Report ── */}
              {activeScan.report && (
                <div className="world-report">
                  <div className="world-section-label">── reporte ──</div>
                  <div className="world-report-score">
                    <span className="dim">riesgo </span>
                    <span className={riskClass(activeScan.report.risk_score)}>
                      {activeScan.report.risk_score ?? "?"}/100
                    </span>
                  </div>

                  {activeScan.report.executive_summary && (
                    <div className="world-report-summary">
                      {activeScan.report.executive_summary}
                    </div>
                  )}

                  {activeScan.report.confirmed_findings?.length ? (
                    <>
                      <div className="world-report-heading">hallazgos confirmados</div>
                      <ul className="world-report-list">
                        {activeScan.report.confirmed_findings.map((f, i) => (
                          <li key={i}>{f}</li>
                        ))}
                      </ul>
                    </>
                  ) : null}

                  {activeScan.report.single_source_signals?.length ? (
                    <>
                      <div className="world-report-heading">señales de fuente única</div>
                      <ul className="world-report-list dim">
                        {activeScan.report.single_source_signals.map((f, i) => (
                          <li key={i}>{f}</li>
                        ))}
                      </ul>
                    </>
                  ) : null}

                  {activeScan.report.exposed_assets && (
                    <>
                      <div className="world-report-heading">activos expuestos</div>
                      <ul className="world-report-list">
                        {(Array.isArray(activeScan.report.exposed_assets)
                          ? activeScan.report.exposed_assets
                          : Object.entries(activeScan.report.exposed_assets).map(
                            ([k, v]) => `${k}: ${JSON.stringify(v)}`,
                          )
                        ).map((f, i) => (
                          <li key={i}>{String(f)}</li>
                        ))}
                      </ul>
                    </>
                  )}

                  {activeScan.report.cves?.length ? (
                    <>
                      <div className="world-report-heading">vulnerabilidades (CVEs)</div>
                      <div style={{ overflowX: "auto" }}>
                        <table className="world-report-cve-table">
                          <thead>
                            <tr>
                              <th>CVE</th>
                              <th>CVSS</th>
                              <th>Fuente</th>
                              <th>Descripción</th>
                            </tr>
                          </thead>
                          <tbody>
                            {activeScan.report.cves.map((c: any, i: number) => (
                              <tr key={i}>
                                <td style={{ color: "#ff4444", whiteSpace: "nowrap" }}>{c.id}</td>
                                <td style={{ color: (c.cvss ?? 0) >= 7 ? "#ff4444" : (c.cvss ?? 0) >= 4 ? "#f5a623" : "#39FF14", textAlign: "center" }}>
                                  {c.cvss ?? "—"}
                                </td>
                                <td className="dim">{c.source ?? "—"}</td>
                                <td className="dim" style={{ fontSize: "11px" }}>{c.description ?? ""}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : null}

                  {activeScan.report.recommended_actions?.length ? (
                    <>
                      <div className="world-report-heading">acciones recomendadas</div>
                      <ul className="world-report-list">
                        {activeScan.report.recommended_actions.map((f, i) => (
                          <li key={i}>{f}</li>
                        ))}
                      </ul>
                    </>
                  ) : null}

                  {activeScan.report.reputation && (
                    <>
                      <div className="world-report-heading">reputación</div>
                      <dl className="world-report-kv">
                        {Object.entries(activeScan.report.reputation).map(([k, v]) => (
                          <div key={k} style={{ display: "contents" }}>
                            <dt>{k}</dt>
                            <dd>{typeof v === "object" ? JSON.stringify(v) : String(v)}</dd>
                          </div>
                        ))}
                      </dl>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── cameras results ── */}
          {module === "cameras" && camResults.length > 0 && (
            <div className="world-section">
              <div className="world-section-label">
                ── results · {camResults.length} · page {camPage} ──
              </div>
              <div className="world-ai-results">
                {camResults.map((r, i) => {
                  const key = `${r.ip}:${r.port}`;
                  const stateLabel = r.accessible ? "abierto" : r.auth_required ? "auth" : `${r.status ?? "?"}`;
                  const stateCls = r.accessible ? "amber" : r.auth_required ? "" : "dim";
                  const bypassDone = r.bypass_url != null;
                  const bypassOk = r.bypass_url && r.bypass_url !== "__nope__";
                  return (
                    <div key={`${key}-${i}`} className="world-ai-row">
                      <div className="world-ai-main">
                        <a
                          className="world-ai-ip"
                          href={r.url}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: "#39FF14" }}
                        >
                          {r.ip}:{r.port}
                        </a>
                        <span className="dim" style={{ fontSize: "10px" }}>{r.brand || "—"}</span>
                        <span className="dim" style={{ fontSize: "10px" }}>
                          {r.country}{r.city ? ` · ${r.city}` : ""}
                        </span>
                        <span className={stateCls} style={{ fontSize: "10px" }}>{stateLabel}</span>
                        {bypassOk ? (
                          <a
                            href={r.bypass_url!}
                            target="_blank"
                            rel="noreferrer"
                            className="amber"
                            style={{ fontSize: "10px" }}
                          >
                            ver imagen
                          </a>
                        ) : bypassDone ? (
                          <span className="dim" style={{ fontSize: "10px" }}>sin acceso</span>
                        ) : (
                          <button
                            className="world-probe-btn"
                            onClick={() => tryBypass(r)}
                            disabled={!r.brand || bypassing === key}
                            style={{ fontSize: "10px" }}
                          >
                            {bypassing === key ? "..." : "bypass"}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="world-cam-pagination">
                <button
                  className="btn-sm"
                  disabled={busy || camPage <= 1}
                  onClick={() => {
                    camPageTriggerRef.current = true;
                    setCamPage((p) => Math.max(1, p - 1));
                  }}
                >
                  [ ← prev ]
                </button>
                <span className="dim world-cam-pagination-info">
                  page <b>{camPage}</b>
                </span>
                <button
                  className="btn-sm"
                  disabled={busy || !camHasMore}
                  onClick={() => {
                    camPageTriggerRef.current = true;
                    setCamPage((p) => p + 1);
                  }}
                >
                  [ next → ]
                </button>
              </div>
            </div>
          )}

          {/* ── ai-sec results + probe ── */}
          {module === "ai-sec" && aiResults.length > 0 && (
            <div className="world-section">
              <div className="world-section-label">── results · {aiResults.length} ──</div>
              {Object.keys(probeResults).length > 0 && (
                <div className="world-probe-stats">
                  probed <b>{Object.keys(probeResults).length}</b>
                  {" · "}
                  <span style={{ color: "#39FF14" }}>
                    ok {Object.values(probeResults).filter((p) => p.score === `${p.max_score}/${p.max_score}`).length}
                  </span>
                </div>
              )}
              <div className="world-ai-results">
                {aiResults.map((r, i) => {
                  const key = `${r.ip}:${r.port}`;
                  const probe = probeResults[key];
                  const isProbing = probing === key;
                  const isExpanded = expandedProbe === key;
                  const riskCls = r.riskLevel === "critical" ? "red" : r.riskLevel === "high" ? "amber" : "";
                  return (
                    <div key={`${key}-${i}`} className="world-ai-row">
                      <div className="world-ai-main">
                        <span className="world-ai-ip">{r.ip}:{r.port}</span>
                        <span className={riskCls} style={{ fontSize: "10px" }}>
                          {r.riskLevel.toUpperCase()}
                        </span>
                        <span className="dim" style={{ fontSize: "10px" }}>
                          {r.country}{r.city ? ` · ${r.city}` : ""}
                        </span>
                        {isProbing ? (
                          <span className="amber" style={{ fontSize: "10px" }}>probing<span className="caret" /></span>
                        ) : probe ? (
                          <span
                            className="world-probe-badge"
                            style={{
                              color: probe.score === `${probe.max_score}/${probe.max_score}` ? "#39FF14" : probe.score === "0/2" ? "#ff4444" : "#ff9500",
                            }}
                            onClick={() => setExpandedProbe(isExpanded ? null : key)}
                          >
                            {probe.score} {isExpanded ? "▾" : "▸"}
                          </span>
                        ) : (
                          <button
                            className="world-probe-btn"
                            onClick={() => executeProbe(r.ip, r.port, r.probeType)}
                            disabled={!!probing}
                          >
                            ▸ probe
                          </button>
                        )}
                      </div>
                      {isExpanded && probe && (
                        <div className="world-probe-detail">
                          <div>
                            <span className={probe.version ? "probe-ok" : "probe-fail"}>
                              {probe.version ? "✔" : "✗"}
                            </span>
                            <span className="dim"> version </span>
                            {probe.version
                              ? typeof probe.version === "object" && "version" in probe.version
                                ? String((probe.version as Record<string, unknown>).version)
                                : JSON.stringify(probe.version)
                              : "no response"}
                          </div>
                          <div>
                            <span className={probe.tags ? "probe-ok" : "probe-fail"}>
                              {probe.tags ? "✔" : "✗"}
                            </span>
                            <span className="dim"> tags </span>
                            {probe.tags ? `${probe.models_count} modelo${probe.models_count !== 1 ? "s" : ""}` : "no response"}
                          </div>
                          {probe.model_names?.length > 0 && (
                            <div className="world-probe-models">
                              {probe.model_names.map((n) => (
                                <div key={n} className="dim">· {n}</div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="world-cam-pagination">
                <button
                  className="btn-sm"
                  disabled={busy || aiPage <= 1}
                  onClick={() => {
                    aiPageTriggerRef.current = true;
                    setAiPage((p) => Math.max(1, p - 1));
                  }}
                >
                  [ ← prev ]
                </button>
                <span className="dim world-cam-pagination-info">
                  page <b>{aiPage}</b>
                </span>
                <button
                  className="btn-sm"
                  disabled={busy || !aiHasMore}
                  onClick={() => {
                    aiPageTriggerRef.current = true;
                    setAiPage((p) => p + 1);
                  }}
                >
                  [ next → ]
                </button>
              </div>
            </div>
          )}

          {/* ── agent-sec results + probe ── */}
          {module === "agent-sec" && agentResults.length > 0 && (
            <div className="world-section">
              <div className="world-section-label">── results · {agentResults.length} ──</div>
              {Object.keys(probeResults).length > 0 && (
                <div className="world-probe-stats">
                  probed <b>{Object.keys(probeResults).length}</b>
                  {" · "}
                  <span style={{ color: "#39FF14" }}>
                    ok {Object.values(probeResults).filter((p) => {
                      const [n, d] = p.score.split("/").map(Number);
                      return n === d;
                    }).length}
                  </span>
                </div>
              )}
              <div className="world-ai-results">
                {agentResults.map((r, i) => {
                  const key = `${r.ip}:${r.port}`;
                  const probe = probeResults[key];
                  const isProbing = probing === key;
                  const isExpanded = expandedProbe === key;
                  const riskCls = r.riskLevel === "critical" ? "red" : r.riskLevel === "high" ? "amber" : "";
                  const isMdns = r.port === 5353;
                  return (
                    <div key={`${key}-${i}`} className="world-ai-row">
                      <div className="world-ai-main">
                        <span className="world-ai-ip">{r.ip}:{r.port}</span>
                        <span className={riskCls} style={{ fontSize: "10px" }}>
                          {r.riskLevel.toUpperCase()}
                        </span>
                        <span className="dim" style={{ fontSize: "10px" }}>
                          {r.country}{r.city ? ` · ${r.city}` : ""}
                        </span>
                        {isMdns ? (
                          <span className="dim" style={{ fontSize: "10px" }}>mDNS</span>
                        ) : isProbing ? (
                          <span className="amber" style={{ fontSize: "10px" }}>probing<span className="caret" /></span>
                        ) : probe ? (
                          <span
                            className="world-probe-badge"
                            style={{
                              color: (() => { const [n, d] = probe.score.split("/").map(Number); return n === d ? "#39FF14" : n > 0 ? "#ff9500" : "#ff4444"; })(),
                            }}
                            onClick={() => setExpandedProbe(isExpanded ? null : key)}
                          >
                            {probe.score} {isExpanded ? "▾" : "▸"}
                          </span>
                        ) : (
                          <button
                            className="world-probe-btn"
                            onClick={() => executeProbe(r.ip, r.port, r.probeType)}
                            disabled={!!probing}
                          >
                            ▸ probe
                          </button>
                        )}
                      </div>
                      {isExpanded && probe && (
                        <div className="world-probe-detail">
                          <div>
                            <span className={probe.service_confirmed ? "probe-ok" : "probe-fail"}>
                              {probe.service_confirmed ? "✔" : "✗"}
                            </span>
                            <span className="dim"> service </span>
                            {probe.service_confirmed ? "confirmado" : "no confirmado"}
                            {probe.title ? ` · ${probe.title}` : ""}
                          </div>
                          <div>
                            <span className={probe.version ? "probe-ok" : "probe-fail"}>
                              {probe.version ? "✔" : "✗"}
                            </span>
                            <span className="dim"> version </span>
                            {probe.version
                              ? typeof probe.version === "object" && "version" in probe.version
                                ? String((probe.version as Record<string, unknown>).version)
                                : JSON.stringify(probe.version)
                              : "unknown"}
                          </div>
                          <div>
                            <span style={{ color: probe.auth_type === "none" ? "#ff4444" : probe.auth_type === "unknown" ? "#ff9500" : "#39FF14" }}>●</span>
                            <span className="dim"> auth </span>
                            <span style={{ color: probe.auth_type === "none" ? "#ff4444" : probe.auth_type === "unknown" ? "#ff9500" : "#39FF14" }}>
                              {AGENT_AUTH_LABELS[probe.auth_type ?? "unknown"] ?? probe.auth_type}
                            </span>
                          </div>
                          {probe.http_status && (
                            <div className="dim">
                              · http {probe.http_status}{probe.server_header ? ` · ${probe.server_header}` : ""}
                            </div>
                          )}
                          {probe.cve_matches?.length ? (
                            <div>
                              <span className="red">!</span>
                              <span className="dim"> cves </span>
                              <span className="red">{probe.cve_matches.join(" · ")}</span>
                            </div>
                          ) : null}
                          <div className="dim" style={{ fontSize: "10px" }}>
                            risk_score <b className="red">{probe.risk_score ?? "?"}</b>/10
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="world-cam-pagination">
                <button
                  className="btn-sm"
                  disabled={busy || agentPage <= 1}
                  onClick={() => {
                    agentPageTriggerRef.current = true;
                    setAgentPage((p) => Math.max(1, p - 1));
                  }}
                >
                  [ ← prev ]
                </button>
                <span className="dim world-cam-pagination-info">
                  page <b>{agentPage}</b>
                </span>
                <button
                  className="btn-sm"
                  disabled={busy || !agentHasMore}
                  onClick={() => {
                    agentPageTriggerRef.current = true;
                    setAgentPage((p) => p + 1);
                  }}
                >
                  [ next → ]
                </button>
              </div>
            </div>
          )}

          {/* ── bd-sec results ── */}
          {module === "bd-sec" && bdResults.length > 0 && (
            <div className="world-section">
              <div className="world-section-label">── results · {bdResults.length} ──</div>
              <div className="world-ai-results">
                {bdResults.map((r, i) => {
                  const key = `${r.ip}:${r.port}:${i}`;
                  const riskCls = r.riskLevel === "critical" ? "red" : r.riskLevel === "high" ? "amber" : r.riskLevel === "low" ? "dim" : "";
                  const catCls = r.category === "database" ? "red" : r.category === "storage" ? "amber" : r.category === "panel" ? "" : "dim";
                  const dataStr = r.dataSize || (
                    r.collectionsCount != null ? `${r.collectionsCount} col` :
                      r.indicesCount != null ? `${r.indicesCount} idx` :
                        r.filesCount != null ? `${r.filesCount} files` : ""
                  );
                  return (
                    <div key={key} className="world-ai-row">
                      <div className="world-ai-main">
                        <span className="world-ai-ip">{r.ip}:{r.port}</span>
                        <span className={catCls} style={{ fontSize: "10px" }}>{r.service}</span>
                        {dataStr && <span className="amber" style={{ fontSize: "10px" }}>{dataStr}</span>}
                        <span className="dim" style={{ fontSize: "10px" }}>
                          {r.countryCode}{r.city ? ` · ${r.city}` : ""}
                        </span>
                        <span className={riskCls} style={{ fontSize: "10px" }}>
                          {r.riskLevel.toUpperCase()}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="world-cam-pagination">
                <button
                  className="btn-sm"
                  disabled={busy || bdPage <= 1}
                  onClick={() => {
                    bdPageTriggerRef.current = true;
                    setBdPage((p) => Math.max(1, p - 1));
                  }}
                >
                  [ ← prev ]
                </button>
                <span className="dim world-cam-pagination-info">
                  page <b>{bdPage}</b>
                </span>
                <button
                  className="btn-sm"
                  disabled={busy || !bdHasMore}
                  onClick={() => {
                    bdPageTriggerRef.current = true;
                    setBdPage((p) => p + 1);
                  }}
                >
                  [ next → ]
                </button>
              </div>
            </div>
          )}

          {/* ── host-recon results + smart probe ── */}
          {module === "host-recon" && reconResults.length > 0 && (
            <div className="world-section">
              <div className="world-section-label">── results · {reconResults.length} ──</div>
              {Object.keys(probeResults).length > 0 && (
                <div className="world-probe-stats">
                  probed <b>{Object.keys(probeResults).length}</b>
                  {" · "}
                  <span style={{ color: "#39FF14" }}>
                    ok {Object.values(probeResults).filter((p) => {
                      const [n, d] = p.score.split("/").map(Number);
                      return n === d;
                    }).length}
                  </span>
                </div>
              )}
              <div className="world-ai-results">
                {reconResults.map((r, i) => {
                  const key = `${r.ip}:${r.port}`;
                  const probe = probeResults[key];
                  const isProbing = probing === key;
                  const isExpanded = expandedProbe === key;
                  const pt = reconProbeType(r.port);
                  const probeLabel = pt === "ollama" ? "ai-sec" : pt === "openclaw" ? "agent-sec" : null;
                  return (
                    <div key={`${key}-${i}`} className="world-ai-row">
                      <div className="world-ai-main">
                        <span className="world-ai-ip">{r.ip}:{r.port}</span>
                        <span className="dim" style={{ fontSize: "10px" }}>
                          {r.service !== "unknown" ? r.service : r.bannerPreview?.slice(0, 20) || "—"}
                        </span>
                        <span className="dim" style={{ fontSize: "10px" }}>
                          {r.country}{r.city ? ` · ${r.city}` : ""}
                        </span>
                        {pt && !probe && !isProbing && (
                          <button
                            className="world-probe-btn"
                            onClick={() => executeProbe(r.ip, r.port, pt)}
                            disabled={!!probing}
                            title={`probe ${probeLabel}`}
                          >
                            ▸ probe {probeLabel}
                          </button>
                        )}
                        {!pt && (
                          <span className="dim" style={{ fontSize: "10px" }}>—</span>
                        )}
                        {isProbing && (
                          <span className="amber" style={{ fontSize: "10px" }}>probing<span className="caret" /></span>
                        )}
                        {probe && (
                          <span
                            className="world-probe-badge"
                            style={{
                              color: (() => { const [n, d] = probe.score.split("/").map(Number); return n === d ? "#39FF14" : n > 0 ? "#ff9500" : "#ff4444"; })(),
                            }}
                            onClick={() => setExpandedProbe(isExpanded ? null : key)}
                          >
                            {probe.score} {isExpanded ? "▾" : "▸"}
                          </span>
                        )}
                      </div>
                      {isExpanded && probe && probe.probe_type === "ollama" && (
                        <div className="world-probe-detail">
                          <div>
                            <span className={probe.version ? "probe-ok" : "probe-fail"}>
                              {probe.version ? "✔" : "✗"}
                            </span>
                            <span className="dim"> version </span>
                            {probe.version
                              ? typeof probe.version === "object" && "version" in probe.version
                                ? String((probe.version as Record<string, unknown>).version)
                                : JSON.stringify(probe.version)
                              : "no response"}
                          </div>
                          <div>
                            <span className={probe.tags ? "probe-ok" : "probe-fail"}>
                              {probe.tags ? "✔" : "✗"}
                            </span>
                            <span className="dim"> tags </span>
                            {probe.tags ? `${probe.models_count} modelo${probe.models_count !== 1 ? "s" : ""}` : "no response"}
                          </div>
                          {probe.model_names?.length > 0 && (
                            <div className="world-probe-models">
                              {probe.model_names.map((n) => (
                                <div key={n} className="dim">· {n}</div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      {isExpanded && probe && probe.probe_type === "openclaw" && (
                        <div className="world-probe-detail">
                          <div>
                            <span className={probe.service_confirmed ? "probe-ok" : "probe-fail"}>
                              {probe.service_confirmed ? "✔" : "✗"}
                            </span>
                            <span className="dim"> service </span>
                            {probe.service_confirmed ? "confirmado" : "no confirmado"}
                            {probe.title ? ` · ${probe.title}` : ""}
                          </div>
                          <div>
                            <span className={probe.version ? "probe-ok" : "probe-fail"}>
                              {probe.version ? "✔" : "✗"}
                            </span>
                            <span className="dim"> version </span>
                            {probe.version
                              ? typeof probe.version === "object" && "version" in probe.version
                                ? String((probe.version as Record<string, unknown>).version)
                                : JSON.stringify(probe.version)
                              : "unknown"}
                          </div>
                          <div>
                            <span style={{ color: probe.auth_type === "none" ? "#ff4444" : probe.auth_type === "unknown" ? "#ff9500" : "#39FF14" }}>●</span>
                            <span className="dim"> auth </span>
                            <span style={{ color: probe.auth_type === "none" ? "#ff4444" : probe.auth_type === "unknown" ? "#ff9500" : "#39FF14" }}>
                              {probe.auth_type ?? "unknown"}
                            </span>
                          </div>
                          {probe.http_status && (
                            <div className="dim">
                              · http {probe.http_status}{probe.server_header ? ` · ${probe.server_header}` : ""}
                            </div>
                          )}
                          {probe.cve_matches?.length ? (
                            <div>
                              <span className="red">!</span>
                              <span className="dim"> cves </span>
                              <span className="red">{probe.cve_matches.join(" · ")}</span>
                            </div>
                          ) : null}
                          <div className="dim" style={{ fontSize: "10px" }}>
                            risk_score <b className="red">{probe.risk_score ?? "?"}</b>/10
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Intel ── */}
          {(totalVisible > 0 || activeScan) && (
            <div className="world-section">
              <div className="world-section-label">── intel ──</div>
              <div className="world-stats-total">
                {totalVisible} targets plotted
              </div>
              {(Object.entries(counts) as [ModuleKey, number][]).map(
                ([k, v]) =>
                  v > 0 && (
                    <div
                      key={k}
                      className="world-stat-row"
                      onClick={() => setModule(k)}
                      style={{ cursor: "pointer" }}
                      title={`Ver resultados de ${k}`}
                    >
                      <span className="world-dot" style={{ background: MODULE_COLORS[k] }} />
                      <span style={{ textDecoration: module === k ? "underline" : "none" }}>{k}</span>
                      <span className="world-stat-n">{v}</span>
                    </div>
                  ),
              )}
            </div>
          )}
          </>}
        </div>
      )}

      {/* ── Bottom HUD ── */}
      <div className="world-hud-bottom">
        <span className="glyph">└──</span>
        <span>
          LAT <b>{coords.lat.toFixed(4)}</b>
        </span>
        <span className="world-sep">·</span>
        <span>
          LNG <b>{coords.lng.toFixed(4)}</b>
        </span>
        <span className="world-sep">·</span>
        <span>
          ZOOM <b>{zoom.toFixed(1)}</b>
        </span>
        {totalVisible > 0 && (
          <>
            <span className="world-sep">·</span>
            <span>
              MARKERS <b>{totalVisible}</b>
            </span>
          </>
        )}
        {(busy || activeScan?.state === "running") && (
          <>
            <span className="world-sep">·</span>
            <span className="amber">
              {activeScan?.state === "running" ? "scan active" : "scanning"}
              <span className="caret" />
            </span>
          </>
        )}
        <span className="glyph" style={{ marginLeft: "auto" }}>
          ──┘
        </span>
      </div>
    </div>
  );
}
